import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const slides = [
  {
    id: 1,
    image: 'https://customer-assets.emergentagent.com/job_majordhome-care/artifacts/0jpn0feu_joyeux-jardinier-plantant-des-semis.jpg',
    alt: 'Jardinier professionnel heureux au travail',
    type: 'image',
    position: 'center 70%'
  },
  {
    id: 2,
    image: 'https://customer-assets.emergentagent.com/job_majord-booking/artifacts/clhxuxyv_smiling-housewife-apron-standing-kitchen.jpg',
    alt: 'Intervenante professionnelle ménage souriante',
    type: 'image',
    position: 'center 25%'
  },
  {
    id: 3,
    image: 'https://images.unsplash.com/photo-1606788075819-9574a6edfab3?w=1920&q=80',
    alt: 'Famille heureuse à la maison',
    type: 'image',
    position: 'center center'
  },
  {
    id: 4,
    image: 'https://images.unsplash.com/photo-1758518729459-235dcaadc611?w=1920&q=80',
    alt: 'Membre de l\'équipe souriant',
    type: 'image',
    position: 'center center'
  },
  {
    id: 5,
    image: 'https://customer-assets.emergentagent.com/job_majordhome-care/artifacts/g28flyry_smiley-femme-dans-la-voiture-lors-d-un-road-trip.jpg',
    alt: 'Cliente heureuse et soulagée',
    type: 'image',
    position: '65% center',
    mobilePosition: '80% 40%'
  },
  {
    id: 6,
    image: 'https://images.unsplash.com/photo-1544984243-ec57ea16fe25?w=1920&q=80',
    alt: 'Belle propriété pour gestion Airbnb',
    type: 'image',
    position: 'center center'
  }
];

export const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className="relative h-screen w-full overflow-hidden font-['Poppins']">
      {/* Slides */}
      {slides.map((slide, index) => {
        const objectPosition = (isMobile && slide.mobilePosition) 
          ? slide.mobilePosition 
          : (slide.position || 'center center');
        
        return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover"
              style={{ objectPosition }}
            />
            {/* Overlay chaleureux */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A2320]/90 via-[#0A2320]/70 to-[#153254]/50"></div>
          </div>
        );
      })}

      {/* Content */}
      <div className="absolute inset-0 z-10 flex items-center">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* H1 - Style chaleureux et dynamique */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 md:mb-6 text-white leading-[1.1] drop-shadow-lg">
              Déléguez, respirez
            </h1>
            
            {/* H2 - Sous-titre */}
            <h2 className="text-xl sm:text-2xl md:text-3xl font-medium mb-8 md:mb-10 text-white/90 drop-shadow-md">
              Votre confort, notre priorité
            </h2>

            {/* Credit impot - Style moderne avec arrondis */}
            <div className="bg-white rounded-2xl p-5 md:p-6 mb-8 md:mb-10 inline-block shadow-2xl max-w-xl">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#153254] mb-2">
                    50% de crédit d'impôt
                  </p>
                  <p className="text-base sm:text-lg text-[#0A2320] font-semibold mb-1">
                    Avance immédiate
                  </p>
                  <p className="text-sm text-gray-500">
                    Sans attendre votre déclaration fiscale
                  </p>
                </div>
                <div className="flex-shrink-0 hidden sm:block">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_concierge-website/artifacts/fyr6pcjx_Traceur_SAP_buro_rvb_png.png" 
                    alt="Service à la Personne" 
                    className="h-14 md:h-16 object-contain opacity-90"
                  />
                </div>
              </div>
            </div>

            {/* CTAs - TOUS EN VERT #0A2320 */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Link to="/devis-premium" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="bg-[#0A2320] hover:bg-[#0d2e2a] text-white text-base md:text-lg px-6 md:px-8 py-5 md:py-6 font-semibold shadow-xl rounded-xl w-full transition-all hover:-translate-y-1"
                >
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A2320] text-base md:text-lg px-6 md:px-8 py-5 md:py-6 font-semibold backdrop-blur-sm w-full sm:w-auto rounded-xl transition-all"
                onClick={() => window.location.href = 'tel:0981543842'}
              >
                <Phone className="mr-2 h-5 w-5" />
                09 81 54 38 42
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation arrows - Style arrondi */}
      <button
        onClick={prevSlide}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 md:p-4 rounded-full transition-all hover:scale-110"
        aria-label="Slide précédent"
      >
        <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 md:p-4 rounded-full transition-all hover:scale-110"
        aria-label="Slide suivant"
      >
        <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
      </button>

      {/* Dots indicator */}
      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-2.5 rounded-full transition-all ${
              index === currentSlide ? 'bg-[#D4AF37] w-8 md:w-10' : 'bg-white/50 w-2.5'
            }`}
            aria-label={`Aller au slide ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Scroll indicator - Mobile only */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 md:hidden animate-bounce">
        <div className="flex flex-col items-center gap-1">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};
